import type {
  CompatibleLocalAgeCategory as catAge,
  CompatibleLocalWeightCategory as catPoid,
  CompatibleLocalDiscipline as disciplines,
} from "../types.js";
import type { AthletesModel, GroupModel } from "../types.js";
import {
  athleteRecordFromValues,
  groupRecordFromValues,
} from "../custom-discipline-behavior/index.js";
import { buildInjectedDataFromElectron } from "../layout/adapter.js";
import type { CustomDisciplineGroupType } from "./types.js";
import { projectCustomDisciplineCompetition } from "./integration-projection.js";
import { customDisciplineRuntimeMetadata } from "./runtime-data.js";
import type {
  DisciplineAgeRule,
  DisciplineWeightRule,
} from "./category-catalog.js";
import { withCustomDisciplineCategoryReferences } from "./category-references.js";
import { readCustomResultsView } from "./results-view.js";

/** Native result layouts keep their custom format; other groups retain legacy adapters. */
export function buildCustomDisciplineResultsGroups(
  groups: GroupModel[],
  compatibleGroups: GroupModel[],
  schemas: readonly CustomDisciplineGroupType[],
): GroupModel[] {
  const nativeTypes = new Set(
    schemas
      .filter((schema) => readCustomResultsView(schema.displayLayouts))
      .map((schema) => `CUSTOM:${schema.id}`),
  );
  const compatible = new Map(
    compatibleGroups.map((group) => [group.id, group]),
  );
  return groups.flatMap((group) => {
    if (nativeTypes.has(group.competitionFormat ?? "")) return [group];
    const adapted = compatible.get(group.id);
    return adapted ? [adapted] : [];
  });
}

/** Compatibility boundary for the existing competition tools. All values come from Behavior. */
export function buildCustomDisciplineCompetitionView(
  groups: GroupModel[],
  legacyAthletes: AthletesModel[],
  schemas: readonly CustomDisciplineGroupType[],
  references: {
    ageCategories: catAge[];
    weightCategories: catPoid[];
    disciplines: disciplines[];
    disciplineAgeCategories?: DisciplineAgeRule[];
    disciplineWeightCategories?: DisciplineWeightRule[];
  },
  meet: { startdate: Date | string; [key: string]: unknown },
) {
  const sources = withCustomDisciplineCategoryReferences({
    ...buildInjectedDataFromElectron(references),
    disciplineAgeCategories: references.disciplineAgeCategories ?? [],
    disciplineWeightCategories: references.disciplineWeightCategories ?? [],
  });
  const resultGroups: GroupModel[] = [];
  const legacyGroups = new Map(
    groups
      .filter(
        (group) =>
          !group.customDisciplineGroupId &&
          !group.competitionFormat?.startsWith("CUSTOM:"),
      )
      .map((group) => [group.id, group]),
  );
  const athletes: AthletesModel[] = legacyAthletes
    .filter((athlete) => legacyGroups.has(athlete.group_id))
    .map((athlete) => ({
      ...athlete,
      group: legacyGroups.get(athlete.group_id)!,
    }));
  const unsupportedGroups: string[] = [];
  const issues: { groupId: string; groupName: string; message: string }[] = [];
  const rejectGroup = (group: GroupModel, message: string) => {
    unsupportedGroups.push(group.nom);
    issues.push({ groupId: group.id, groupName: group.nom, message });
  };
  for (const group of groups) {
    if (
      !group.customDisciplineGroupId &&
      !group.competitionFormat?.startsWith("CUSTOM:")
    ) {
      resultGroups.push(group);
      continue;
    }
    const custom = group.customDisciplineGroup;
    const schema = schemas.find((item) => item.id === custom?.groupTypeId);
    if (!custom || !schema) {
      rejectGroup(
        group,
        "Configuration administrateur ou adaptateur de compétition absent",
      );
      continue;
    }
    try {
      const groupRecord = {
        ...customDisciplineRuntimeMetadata(group),
        categoryDisciplineId: schema.discipline?.categoryDisciplineId ?? null,
        ...groupRecordFromValues(
          schema.fields.filter((field) => field.scope === "GROUP") as never,
          custom.values as never,
          {
            id: group.id,
            groupId: group.id,
            referenceSources: sources as never,
          },
        ),
      };
      const records = custom.athletes.map((athlete) =>
        athleteRecordFromValues(
          schema.fields.filter((field) => field.scope === "ATHLETE") as never,
          athlete.values as never,
          {
            id: athlete.id,
            groupId: group.id,
            referenceSources: sources as never,
          },
        ),
      );
      const projected = projectCustomDisciplineCompetition(schema, {
        ...sources,
        athlete: undefined,
        group: groupRecord,
        athletes: records,
        currentMeet: {
          ...meet,
          startdate: new Date(meet.startdate).toISOString(),
        },
        instanceKey: `competition:${group.id}`,
        defaultText: "",
      });
      if (!projected) {
        rejectGroup(
          group,
          "Configuration administrateur ou adaptateur de compétition absent",
        );
        continue;
      }
      const adaptedGroup = { ...group, competitionFormat: projected.format };
      const adaptedAthletes = projected.athletes.map((row) => {
        const ageCategory = references.ageCategories.find(
          (item) => item.id === Number(row.catAgeId),
        );
        const weightCategory = references.weightCategories.find(
          (item) => item.id === Number(row.catPoidId),
        );
        const discipline = references.disciplines.find(
          (item) => item.id === Number(row.disciplineId),
        );
        const dateNaissance = new Date(String(row.dateNaissance));
        if (
          !ageCategory ||
          !weightCategory ||
          !discipline ||
          !Number.isFinite(dateNaissance.getTime())
        )
          throw new Error(
            `Vérifiez les catégories, la discipline et la date de naissance du groupe ${group.nom}`,
          );
        return {
          ...row,
          dateNaissance,
          group_id: group.id,
          group: adaptedGroup,
          catAgeId: ageCategory.id,
          catAge: ageCategory,
          catPoidId: weightCategory.id,
          catPoid: weightCategory,
          disciplineId: discipline.id,
          discipline,
        } as unknown as AthletesModel;
      });
      resultGroups.push(adaptedGroup);
      athletes.push(...adaptedAthletes);
    } catch (error) {
      rejectGroup(
        group,
        error instanceof Error ? error.message : String(error),
      );
    }
  }
  return { groups: resultGroups, athletes, unsupportedGroups, issues };
}
